import { useEffect, useRef } from 'react';
import { ArrowClockwise, MapPinSimple, X } from '@phosphor-icons/react';
import type { LocationProblem } from '../geolocate';
import { isAndroid, isIos, isStandalone } from '../platform';

/**
 * What to do when "Use my location" fails. A home-screen web app has no
 * address bar or site-settings menu, so once iOS has remembered "Don't
 * Allow" the app can't ask again and the only way out is iPhone Settings —
 * this says exactly where, instead of a bare "denied" error.
 */
export default function LocationHelp({
  kind,
  message,
  permission = 'unknown',
  detail = null,
  onRetry,
  onDismiss,
}: {
  kind: LocationProblem;
  message: string;
  /** What the browser had remembered for this page when the request failed. */
  permission?: PermissionState | 'unknown';
  /** Raw error code + environment, shown small for troubleshooting. */
  detail?: string | null;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  const ios = isIos();
  const ref = useRef<HTMLDivElement>(null);
  // On the start screen this lands below the fold; bring it into view.
  useEffect(() => {
    ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, []);
  const app = isStandalone();
  // With Location Services off, iOS reports "unavailable" instead of "denied".
  const needsSettings = kind === 'denied' || (kind === 'unavailable' && ios);

  return (
    <div className="location-help" role="alert" ref={ref}>
      <div className="location-help-head">
        <span className="location-help-icon" aria-hidden="true">
          <MapPinSimple size={16} weight="fill" />
        </span>
        <strong>{needsSettings ? 'Location is turned off for PlaneSight' : message}</strong>
        <button className="icon-btn sm" onClick={onDismiss} aria-label="Dismiss" title="Dismiss">
          <X size={14} weight="bold" />
        </button>
      </div>

      {needsSettings && ios && permission === 'denied' && (
        <>
          <p>
            Your iPhone settings can be right and this still fails: iOS has remembered <b>“Don’t Allow”</b> for this
            {app ? ' icon' : ' page'}, and {app ? 'an installed app' : 'it'} can’t ask again. Reset that:
          </p>
          <ol>
            {app ? (
              <>
                <li>
                  Touch and hold the PlaneSight icon → <b>Remove App</b> → <b>Delete App</b>.
                </li>
                <li>
                  Open <b>{location.host}</b> in <b>Safari</b>, tap Share → <b>Add to Home Screen</b>.
                </li>
                <li>
                  Open PlaneSight from the new icon, tap the location arrow, and choose <b>Allow</b> (or <b>Allow While
                  Using App</b>) when it asks.
                </li>
              </>
            ) : (
              <li>
                In Safari: tap <b>aA</b> in the address bar → <b>Website Settings</b> → <b>Location</b> → <b>Allow</b>, then
                tap Try again.
              </li>
            )}
            <li className="location-help-last">
              Still no prompt? iPhone <b>Settings</b> → <b>General</b> → <b>Transfer or Reset iPhone</b> → <b>Reset</b> →{' '}
              <b>Reset Location &amp; Privacy</b> makes every app ask again (it only resets those choices).
            </li>
          </ol>
        </>
      )}
      {needsSettings && ios && permission !== 'denied' && (
        <ol>
          <li>
            Open the iPhone <b>Settings</b> app, then <b>Privacy &amp; Security</b> → <b>Location Services</b>. Make sure the
            switch at the top is <b>on</b>.
          </li>
          <li>
            In that list, tap <b>Safari Websites</b> and choose <b>While Using the App</b>
            {app ? ' (apps added to the Home Screen use this setting). If PlaneSight is listed too, set it the same way.' : '.'}
          </li>
          <li>
            Come back here, tap <b>Try again</b>, and choose <b>Allow</b> when your iPhone asks.
          </li>
          <li className="location-help-last">
            Still blocked? iOS may have remembered “Don’t Allow” for this {app ? 'icon' : 'page'}
            {app ? (
              <>
                : delete the PlaneSight icon, open <b>{location.host}</b> in Safari, and add it to the Home Screen again.
              </>
            ) : (
              '.'
            )}
          </li>
        </ol>
      )}
      {needsSettings && !ios && isAndroid() && (
        <ol>
          <li>
            Press and hold the PlaneSight icon (or open Chrome’s <b>⋮</b> menu) → <b>Site settings</b> /{' '}
            <b>App info</b> → <b>Permissions</b> → <b>Location</b> → <b>Allow</b>.
          </li>
          <li>
            Come back here and tap <b>Try again</b>.
          </li>
        </ol>
      )}
      {needsSettings && !ios && !isAndroid() && (
        <ol>
          <li>
            Click the lock or tune icon left of the address bar → <b>Site settings</b> → <b>Location</b> → <b>Allow</b>.
          </li>
          <li>
            Come back here and tap <b>Try again</b>.
          </li>
        </ol>
      )}
      {!needsSettings && <p>Move to a spot with a clearer view of the sky or better signal, then try again.</p>}

      <button className="location-help-retry" onClick={onRetry}>
        <ArrowClockwise size={14} weight="bold" aria-hidden="true" />
        Try again
      </button>
      {detail && <p className="location-help-detail mono">{detail}</p>}
    </div>
  );
}
